import type React from 'react';
import { useId } from 'react';
import { Checkbox } from '@shelflix/ui-web/components/ui/checkbox';
import {
  getAllDependentPermissions,
  getAllRequiredPermissions,
  type PermissionDependencyMap,
} from '@/config/permissionDependencies';

interface Permission {
  uuid: string;
  code: string;
  name: string;
  description: string;
}

interface PermissionCheckboxProps {
  permission: Permission;
  allPermissions: Permission[];
  selectedPermissionIds: string[];
  onPermissionsChange: (newPermissionIds: string[]) => void;
  /**
   * Map of permission dependencies from the database.
   * Format: { dependentCode: requiredCodes[] }
   */
  dependencyMap: PermissionDependencyMap;
}

/**
 * A checkbox component for managing permission selection with automatic dependency handling.
 *
 * When a permission is checked:
 * - All permissions it depends on are automatically checked and disabled
 *
 * When a required permission is unchecked:
 * - All permissions that depend on it are automatically unchecked
 *
 * Dependencies come from the database via tRPC endpoint.
 */
export function PermissionCheckbox({
  permission,
  allPermissions,
  selectedPermissionIds,
  onPermissionsChange,
  dependencyMap,
}: PermissionCheckboxProps) {
  const checkboxId = useId();
  const isChecked = selectedPermissionIds.includes(permission.uuid);

  // Check if this permission is required by any currently selected permission
  const { isDisabled, requiredBy } = getDisabledState(
    permission.code,
    selectedPermissionIds,
    allPermissions,
    dependencyMap
  );

  const handleCheckedChange = (checked: boolean) => {
    const newPermissionIds = new Set(selectedPermissionIds);

    if (checked) {
      // Add this permission
      newPermissionIds.add(permission.uuid);

      // Add all required dependencies
      const requiredCodes = getAllRequiredPermissions(permission.code, dependencyMap);
      for (const reqCode of requiredCodes) {
        const reqPermission = allPermissions.find((p) => p.code === reqCode);
        if (reqPermission) {
          newPermissionIds.add(reqPermission.uuid);
        }
      }
    } else {
      // Remove this permission
      newPermissionIds.delete(permission.uuid);

      // Remove all permissions that depend on this one
      const dependentCodes = getAllDependentPermissions(permission.code, dependencyMap);
      for (const depCode of dependentCodes) {
        const depPermission = allPermissions.find((p) => p.code === depCode);
        if (depPermission) {
          newPermissionIds.delete(depPermission.uuid);
        }
      }
    }

    onPermissionsChange(Array.from(newPermissionIds));
  };

  const disabledLabel = requiredBy.length > 0 ? `(requerido por "${requiredBy[0]}")` : '';

  // Show label with all dependencies if there are multiple
  const disabledLabelFull = requiredBy.length > 1 ? `(requerido por: ${requiredBy.join(', ')})` : disabledLabel;

  return (
    <div className="flex flex-row items-start space-y-0 space-x-3">
      <Checkbox id={checkboxId} checked={isChecked} disabled={isDisabled} onCheckedChange={handleCheckedChange} />
      <label htmlFor={checkboxId} className="cursor-pointer text-sm font-normal">
        <div className="flex flex-col">
          <span className="text-base leading-[20px] font-medium text-[var(--gray-primary)]">
            {permission.name}
            {isDisabled && <span className="ml-2 text-xs text-[var(--gray-secondary)]">{disabledLabelFull}</span>}
          </span>
          <span className="font-[12px] font-normal text-[var(--gray-secondary)]">{permission.description}</span>
        </div>
      </label>
    </div>
  );
}

/**
 * Determine if a permission should be disabled based on current selections
 */
function getDisabledState(
  permissionCode: string,
  selectedPermissionIds: string[],
  allPermissions: Permission[],
  dependencyMap: PermissionDependencyMap
): { isDisabled: boolean; requiredBy: string[] } {
  const requiredBy: string[] = [];

  // Get selected permission codes
  const selectedCodes = selectedPermissionIds
    .map((id) => allPermissions.find((p) => p.uuid === id)?.code)
    .filter(Boolean) as string[];

  // Check if any selected permission requires this one
  for (const selectedCode of selectedCodes) {
    const requiredPermissions = getAllRequiredPermissions(selectedCode, dependencyMap);
    if (requiredPermissions.includes(permissionCode)) {
      const selectedPermission = allPermissions.find((p) => p.code === selectedCode);
      if (selectedPermission) {
        requiredBy.push(selectedPermission.name);
      }
    }
  }

  return {
    isDisabled: requiredBy.length > 0,
    requiredBy,
  };
}

export default PermissionCheckbox;
